'use client'

import { useEffect, useState } from 'react'
import { useUser } from '@/contexts/user/UserContext'

/**
 * DOM custom-event name that any client-side code can dispatch to
 * force this badge to refetch its count immediately (bypassing the
 * 60s poll). Exported so consumers can dispatch it after doing
 * something that would change the count — currently used by the
 * thread-detail page after the server auto-marks messages read on
 * GET, so the badge decrements in the same paint tick instead of
 * lying for up to a minute.
 */
export const MESSAGES_BADGE_REFRESH_EVENT = 'messages-badge-refresh'

/**
 * Fire the badge-refresh event. Callers use this after they know
 * they've changed the therapist's unread state.
 */
export function requestMessagesBadgeRefresh() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(MESSAGES_BADGE_REFRESH_EVENT))
}

/**
 * Small poll-driven unread-count pill for the top-nav "Messages"
 * link. Hidden entirely when the count is 0 so the nav stays quiet
 * for pros with no iCBT clients.
 *
 * Refreshes:
 *   - On mount + every 60s (baseline poll)
 *   - When `profile.id` changes (impersonation swap)
 *   - When any client dispatches `MESSAGES_BADGE_REFRESH_EVENT`
 *     (used by the thread page after auto-mark-read so the badge
 *     drops in the same paint tick instead of lagging by up to 60s)
 *
 * Non-realtime by design — matches the imwell messaging cadence and
 * avoids a Supabase realtime channel just for a nav badge.
 */
export function MessagesUnreadBadge() {
  const { profile } = useUser()
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!profile?.id) return
    let cancelled = false
    const fetchCount = async () => {
      try {
        const res = await fetch(`/api/messages/unread-count?proId=${profile.id}`, {
          cache: 'no-store',
        })
        if (!res.ok || cancelled) return
        const j = (await res.json()) as { count: number }
        setCount(j.count ?? 0)
      } catch {
        // Silent — badge failing shouldn't break the nav.
      }
    }
    fetchCount()
    const interval = setInterval(fetchCount, 60_000)
    const onRefresh = () => fetchCount()
    window.addEventListener(MESSAGES_BADGE_REFRESH_EVENT, onRefresh)
    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener(MESSAGES_BADGE_REFRESH_EVENT, onRefresh)
    }
  }, [profile?.id])

  if (count === 0) return null
  return (
    <span
      className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary-500 px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums leading-none"
      title={`${count} unread message${count === 1 ? '' : 's'}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}
